import { GrowthBook, GrowthBookPayload } from '@growthbook/growthbook-react';
import { getEnvironment, ENVIRONMENT_PRODUCTION, ENVIRONMENT_DEVELOPMENT, ENVIRONMENT_PREVIEW, isEndToEndTest } from 'utils/environment';
import configJSON from 'config.json';
import { TenantId, AllowedExperimentKey, AllowedFeatureFlagKey, EXPERIMENT_FEATURE_KEY } from '@tenants/types';
import { getTenantById } from '@tenants';
import { IncomingHttpHeaders } from 'http';
import { ExperimentContext } from 'observability/constants/experiment-events';
import {
  checkTenantConfig,
  checkPayloadCondition,
  handleHeaderOverride,
  evaluateGrowthBookResult,
} from 'services/experiment-circuit-breaker';

const GROWTHBOOK_INIT_TIMEOUT = 2000;

// Log GrowthBook SSR status once at module load
if (isEndToEndTest) {
  console.warn('[GrowthBook:SSR] DISABLED - isEndToEndTest=true. Run with ENABLE_GROWTHBOOK=true to enable.');
}

/** Header name for forcing experiment variations via ModHeader */
export const GB_FORCE_HEADER = 'x-gb-force';

export interface GrowthBookSSRAttributes {
  deviceId?: string;
  tenantId?: TenantId;
  loggedInId?: string | number;
}

export type SSRExperimentResults = Record<AllowedExperimentKey, boolean>;

/**
 * SSR-evaluated feature flag results.
 * Simple boolean flags evaluated via `gb.isOn()` with no tenant gating.
 */
export type SSRFeatureFlagResults = Record<AllowedFeatureFlagKey, boolean>;

/**
 * Metadata about an experiment decision made during SSR.
 * 
 * This metadata is "piped" from SSR to the client so that all experiment logs
 * can be displayed in the browser console with complete context.
 */
export interface ExperimentMetadata {
  /** The variation ID (0 = control, 1 = variation) */
  variationId: number;
  /** Whether the user was bucketed into the experiment */
  inExperiment: boolean;
  /** Whether a URL or header override was applied */
  overrideApplied: boolean;
  /** The source of the override (if applied) */
  overrideSource?: 'url' | 'header';
}

/**
 * Collection of experiment metadata from SSR, keyed by experiment key.
 * Passed through GrowthBook context to enable client-side logging.
 */
export type SSRExperimentMetadata = Partial<Record<AllowedExperimentKey, ExperimentMetadata>>;

/**
 * Complete SSR data returned from getGrowthBookPayload.
 * Includes both the experiment decisions and metadata for logging.
 */
export interface GrowthBookSSRData {
  /** GrowthBook payload for hydration */
  payload: GrowthBookPayload;
  /** Experiment decisions (boolean per experiment) */
  experimentResults: SSRExperimentResults;
  /** Experiment metadata for client-side logging */
  experimentMetadata: SSRExperimentMetadata;
  /** Feature flag decisions (boolean per flag, no tenant gating) */
  featureFlagResults: SSRFeatureFlagResults;
}

/**
 * Parses forced experiment overrides from header.
 * Header format: "experiment-key-1:1,experiment-key-2:0"
 * 
 * @example
 * // ModHeader value: "dj-listing-hero-redesign:1"
 * // Returns: { "dj-listing-hero-redesign": 1 }
 */
export function parseForceHeader(headers?: IncomingHttpHeaders): Record<string, number> {
  const headerValue = headers?.[GB_FORCE_HEADER];
  if (!headerValue || typeof headerValue !== 'string') return {};

  const overrides: Record<string, number> = {};
  headerValue.split(',').forEach(pair => {
    const [key, value] = pair.trim().split(':');
    if (key && value !== undefined) {
      overrides[key.trim()] = parseInt(value.trim(), 10);
    }
  });
  return overrides;
}

/**
 * Parse URL query parameters for experiment overrides.
 * Format: ?gb_force_experiment-key=1
 * 
 * Only works in development and preview environments for security.
 * 
 * @param url - The request URL (can be relative like "/path?query" or absolute)
 * @returns Record of experiment keys to override values
 */
function parseUrlOverrides(url?: string): Record<string, number> {
  if (!url) return {};
  
  // Only allow URL overrides in development and preview environments
  const environment = getEnvironment();
  const isDevelopment = environment === ENVIRONMENT_DEVELOPMENT;
  const isPreview = environment === ENVIRONMENT_PREVIEW;
  
  if (!isDevelopment && !isPreview) {
    return {};
  }
  
  try {
    // URL constructor requires an absolute URL, so provide a dummy base for relative URLs
    // The base doesn't matter - we only care about the query parameters
    const urlObj = new URL(url, 'http://dummy-base');
    const overrides: Record<string, number> = {};
    
    urlObj.searchParams.forEach((value, key) => {
      if (key.startsWith('gb_force_')) {
        const experimentKey = key.replace('gb_force_', '');
        const parsed = parseInt(value, 10);
        if (!isNaN(parsed)) {
          overrides[experimentKey] = parsed;
        }
      }
    });
    
    return overrides;
  } catch (error) {
    console.warn('[GrowthBook:SSR] Failed to parse URL overrides:', error);
    return {};
  }
}

/**
 * Generates GrowthBook payload and evaluates experiments for SSR.
 * 
 * Returns both the payload for client hydration AND pre-evaluated experiment results
 * to avoid flash/flicker during hydration.
 * 
 * IMPORTANT: Experiments are evaluated with THREE layers of validation:
 * 1. Tenant config whitelist (tenant.features.Experiments) - code-level safety net
 * 2. GrowthBook payload condition check - validates dashboard tenant targeting
 * 3. GrowthBook evaluation - applies targeting rules and rollout percentage
 * 
 * This defense-in-depth approach ensures experiments never run on wrong tenants,
 * even if GrowthBook is misconfigured or conditions are missing.
 * 
 * @param attributes - User attributes for experiment targeting (includes tenantId)
 * @param headers - Request headers (for forced overrides via ModHeader)
 * @param url - Request URL (for forced overrides via query parameters)
 */
export async function getGrowthBookPayload(
  attributes?: GrowthBookSSRAttributes,
  headers?: IncomingHttpHeaders,
  url?: string
): Promise<GrowthBookSSRData> {
  const gb = new GrowthBook({
    apiHost: configJSON.growthbook.apiHost,
    clientKey: configJSON.growthbook.clientKey,
    enableDevMode: getEnvironment() !== ENVIRONMENT_PRODUCTION,
    enabled: !isEndToEndTest,
    attributes: attributes ? {
      id: attributes.deviceId,
      anonymous_id: attributes.deviceId,
      loggedIn: attributes.loggedInId,
      tenantId: attributes.tenantId,
      environment: getEnvironment(),
    } : undefined,
  });

  const initResult = await gb.init({ timeout: GROWTHBOOK_INIT_TIMEOUT });
  
  if (!initResult.success) {
    console.warn('[GrowthBook:SSR] Init failed - experiments will default to control', {
      source: initResult.source,
      error: initResult.error?.message,
      tenantId: attributes?.tenantId,
    });
  }

  const payload = gb.getPayload();
  const headerOverrides = parseForceHeader(headers);
  const urlOverrides = parseUrlOverrides(url);
  
  // Merge overrides: URL overrides take precedence over header overrides
  const forceOverrides = { ...headerOverrides, ...urlOverrides };

  // Get tenant config to check which experiments are enabled
  const tenant = attributes?.tenantId ? getTenantById(attributes.tenantId) : null;
  const experimentsConfig = tenant?.features.Experiments || {};

  // Pre-evaluate all experiments for SSR to avoid flash
  // Three layers of validation (unless overridden):
  // 1. Tenant config whitelist (code-level safety)
  // 2. GrowthBook payload condition check (dashboard configuration)
  // 3. GrowthBook evaluation (targeting rules, rollout %)
  //
  // Overrides (header/URL) bypass all layers in dev/preview environments
  const experimentResults: SSRExperimentResults = {} as SSRExperimentResults;
  const experimentMetadata: SSRExperimentMetadata = {};
  
  Object.values(AllowedExperimentKey).forEach(key => {
    // Check for override FIRST - bypasses all circuit breaker checks in dev/preview
    if (key in forceOverrides) {
      const experimentConfig = experimentsConfig[key];
      const isEnabledForTenant = experimentConfig?.enabled ?? false;
      const source = key in urlOverrides ? 'url' : 'header';
      
      const overrideResult = handleHeaderOverride(key, forceOverrides[key], attributes?.tenantId, isEnabledForTenant);
      experimentResults[key] = overrideResult.allowed;
      
      // Store metadata for client-side logging (piped through context)
      experimentMetadata[key] = {
        variationId: forceOverrides[key],
        inExperiment: true,
        overrideApplied: true,
        overrideSource: source,
      };
      
      return;
    }
    
    // Layer 1: Check tenant config
    const layer1Result = checkTenantConfig(key, attributes?.tenantId, ExperimentContext.SSR);
    if (layer1Result) {
      experimentResults[key] = layer1Result.allowed;
      return;
    }
    
    // Layer 2: Check payload condition
    const layer2Result = checkPayloadCondition(key, payload, attributes?.tenantId, ExperimentContext.SSR);
    if (layer2Result) {
      experimentResults[key] = layer2Result.allowed;
      return;
    }
    
    // Layer 3: Run GrowthBook evaluation
    // Uses evalFeature (not the raw run() API) against the experiment's actual
    // Feature Key so GrowthBook's live coverage/weights/targeting are respected -
    // see EXPERIMENT_FEATURE_KEY for why the feature key can differ from `key`.
    const featureResult = gb.evalFeature(EXPERIMENT_FEATURE_KEY[key]);
    const experimentResult = featureResult.experimentResult;
    const evalResult = evaluateGrowthBookResult(
      key,
      experimentResult ? {
        variationId: experimentResult.variationId,
        inExperiment: experimentResult.inExperiment,
        hashUsed: experimentResult.hashUsed,
        value: experimentResult.value,
      } : undefined,
      attributes?.tenantId,
      ExperimentContext.SSR
    );
    experimentResults[key] = evalResult.allowed;

    // Store metadata for client-side logging (piped through context)
    experimentMetadata[key] = {
      variationId: experimentResult?.variationId ?? 0,
      inExperiment: experimentResult?.inExperiment ?? false,
      overrideApplied: false,
    };
  });

  // Pre-evaluate feature flags for SSR (simple boolean flags, no tenant gating)
  // Force overrides (header/URL) apply to feature flags too in dev/preview
  const featureFlagResults: SSRFeatureFlagResults = {} as SSRFeatureFlagResults;
  Object.values(AllowedFeatureFlagKey).forEach(key => {
    if (key in forceOverrides) {
      featureFlagResults[key] = forceOverrides[key] === 1;
      return;
    }
    featureFlagResults[key] = gb.evalFeature(key).on;
  });

  gb.destroy();

  return { payload, experimentResults, experimentMetadata, featureFlagResults };
}